'use client';

import { ReactNode, RefObject, useId } from 'react';
import { FlexibleFormAlert } from '@/components-2.0/contentful/FlexibleForm/FlexibleFormAlert';
import { IconArrow } from '@/assets/icons/IconArrow';
import { getCareBaseUrl } from '@/services/runtimeConfig';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Link } from '@/ui-components/atoms/Link/Link';
import { Text } from '@/ui-components/atoms/Text/Text';
import {
    AppDownloadButtons,
    type DownloadButtonProps,
} from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import { InAppPurchasePopover } from '@/src/ui-components/organisms/Snapshot/InAppPurchasePopover/InAppPurchasePopover';
import { SignInVerificationCodeBody } from '@/ui-components/organisms/Snapshot/SignIn/SignInVerificationCodeBody';
import PartnerSubscription from '@/src/ui-components/organisms/Snapshot/PartnerSubscription/PartnerSubscription';
import { SnapshotPopoverShell } from './SnapshotPopoverShell';
import { SnapshotUserAnonymousSignIn } from './SnapshotUserAnonymousSignIn';
import { SnapshotUserChooseSignInMethod } from './SnapshotUserChooseSignInMethod';
import type { SnapshotAlertBannerProps } from './SnapshotAlertBanner';
import { SnapshotUserPasswordSignIn } from './SnapshotUserPasswordSignIn';
import type { SnapshotUserErrorType, SnapshotUserStep } from './SnapshotUser.types';
import {
    CANADA_ACCOUNT_SIGN_IN_URL,
    PLANS_PATH,
    PLAYER_WELCOME_PATH,
    type SnapshotAuthenticatedUser,
    type TeslaManageSelectionData,
} from './snapshotSignInFlowServices';
import { useSnapshotSignInFlow } from './useSnapshotSignInFlow';
import styles from './snapshotpopover.module.scss';

export type { SnapshotAuthenticatedUser, TeslaManageSelectionData } from './snapshotSignInFlowServices';

const FALLBACK_APP_DOWNLOAD_ITEMS: DownloadButtonProps[] = [
    {
        storeLink: {
            href: 'https://apps.apple.com/us/app/siriusxm-music-sports-news/id317951436',
            target: '_blank',
        },
        badgeSrc: 'https://www.siriusxm.com/content/dam/sxm-com/logos/banners/Download_on_the_App_Store_Badge.svg',
        type: 'appleStore',
    },
    {
        storeLink: {
            href: 'https://play.google.com/store/apps/details?id=com.sirius&hl=en&gl=US',
            target: '_blank',
        },
        badgeSrc: 'https://www.siriusxm.com/content/dam/sxm-com/logos/banners/Google_Play_Store_badge_EN.svg',
        type: 'playStore',
    },
];

interface SnapshotUserPopoverProps {
    appdownloadItem?: DownloadButtonProps[];
    buttonRef: RefObject<HTMLButtonElement | null>;
    id?: string;
    initialAccountCountry?: string;
    initialIdentifier?: string;
    initialNotificationBanner?: SnapshotAlertBannerProps;
    initialTeslaManageSelection?: TeslaManageSelectionData;
    initialErrorType?: SnapshotUserErrorType;
    initialStep?: SnapshotUserStep;
    onAuthenticated?: (data: SnapshotAuthenticatedUser) => void;
    onClose: () => void;
    open: boolean;
}

export function SnapshotUserPopover({
    appdownloadItem,
    buttonRef,
    id,
    open,
    onClose,
    onAuthenticated,
    initialAccountCountry,
    initialIdentifier = '',
    initialNotificationBanner,
    initialTeslaManageSelection,
    initialErrorType = 'none',
    initialStep = 'enterIdentifier',
}: SnapshotUserPopoverProps): React.JSX.Element | null {
    const headingId = useId();
    const resolvedAppDownloadItems = appdownloadItem ?? FALLBACK_APP_DOWNLOAD_ITEMS;
    const {
        accountCountry,
        errorType,
        handleCredentialBack,
        handleIdentifierBlur,
        handleIdentifierChange,
        handleMethodBack,
        handleMethodContinue,
        handlePartnerBack,
        handlePartnerDismiss,
        handlePasswordChange,
        handlePasswordSubmit,
        handleRequestNewCode,
        handleSelectMethod,
        handleSubmit,
        handleVerificationCodeBlur,
        handleVerificationCodeChange,
        handleVerificationCodeSubmit,
        hasSubmittedIdentifierStep,
        hasSubmittedVerificationCodeStep,
        identifier,
        identityState,
        inAppPurchaseSubscribeHref,
        inAppPurchaseVariant,
        isSubmitting,
        methodNotificationBanner,
        methodOptions,
        partnerSubscriptionVariant,
        password,
        selectedMethod,
        selectedMethodDescription,
        step,
        teslaManageSelection,
        verificationCode,
        verificationNotification,
    } = useSnapshotSignInFlow({
        careBaseUrl: getCareBaseUrl(),
        initialAccountCountry,
        initialErrorType,
        initialIdentifier,
        initialNotificationBanner,
        initialStep,
        initialTeslaManageSelection,
        onAuthenticated,
        onClose,
    });
    const isCanadianManagedAccount = accountCountry === 'CA';
    const isRegionManagedAccount = Boolean(accountCountry && accountCountry !== 'US' && accountCountry !== 'CA');
    const isStreamingIdentity = identityState === 'STREAMING';
    const isNonStandardIdentity = Boolean(
        identityState && identityState !== 'STREAMING' && identityState !== 'ACCOUNT',
    );

    let invalidMessage: ReactNode | undefined;
    const isPartnerSubscriptionStep = step === 'partnerSubscription';
    const isTeslaManageSelectionStep = Boolean(teslaManageSelection);
    const shouldShowPartnerSubscriptionFooter =
        !isTeslaManageSelectionStep &&
        inAppPurchaseVariant !== 'expired-subscription' &&
        inAppPurchaseVariant !== 'incomplete-transaction';

    if (errorType === 'blank') {
        invalidMessage = 'Enter your email or username.';
    } else if (errorType === 'notFound') {
        invalidMessage = (
            <span>
                We can&apos;t find a match for the info you entered. Do you want to{' '}
                <Link className={styles.snapshotErrorLink} href={PLANS_PATH}>
                    subscribe to SiriusXM?
                </Link>
            </span>
        );
    } else if (errorType === 'blankPassword') {
        invalidMessage = 'Enter your password.';
    } else if (errorType === 'blankVerificationCode') {
        invalidMessage = 'Enter the verification code we sent you.';
    } else if (errorType === 'invalidCredentials') {
        invalidMessage = 'The password you entered is incorrect.';
    } else if (errorType === 'otpSendFailed') {
        invalidMessage = 'We could not send a verification code right now. Please try again.';
    } else if (errorType === 'otpSendRateLimited') {
        invalidMessage =
            step === 'enterVerificationCode'
                ? undefined
                : "You've reached your verification code limit for now. Try again later.";
    } else if (errorType === 'verificationCodeInvalid') {
        invalidMessage = 'Enter the verification code we sent you.';
    } else if (errorType === 'verificationCodeExpired') {
        invalidMessage = 'That verification code has expired. Request a new code and try again.';
    } else if (errorType === 'sdkUnavailable') {
        invalidMessage = 'Sign-in is temporarily unavailable. Please refresh and try again.';
    }

    return (
        <SnapshotPopoverShell
            ariaLabel="Sign in"
            ariaLabelledBy={headingId}
            buttonRef={buttonRef}
            id={id}
            onClose={onClose}
            open={open}
        >
            {step === 'chooseSignInMethod' ? (
                <SnapshotUserChooseSignInMethod
                    appdownloadItems={resolvedAppDownloadItems}
                    headingId={headingId}
                    methodOptions={methodOptions}
                    notificationBanner={methodNotificationBanner}
                    onBack={handleMethodBack}
                    onContinue={handleMethodContinue}
                    onSelectMethod={handleSelectMethod}
                    selectedMethod={selectedMethod}
                />
            ) : step === 'enterPassword' ? (
                <SnapshotUserPasswordSignIn
                    alert={
                        isCanadianManagedAccount ? (
                            <FlexibleFormAlert
                                tone="warning"
                                message={
                                    <>
                                        It looks like this account is managed by our Canadian account center. Please
                                        visit <Link href={CANADA_ACCOUNT_SIGN_IN_URL}>siriusxm.ca/myaccount</Link> to
                                        sign in.
                                    </>
                                }
                            />
                        ) : isStreamingIdentity ? (
                            <FlexibleFormAlert
                                tone="warning"
                                message={
                                    <>
                                        The username/password you entered is for streaming on the app and to{' '}
                                        <Link href={PLAYER_WELCOME_PATH}>listen online</Link>. Please sign in with your
                                        account login to manage your plan, or chat with an agent for assistance.
                                    </>
                                }
                            />
                        ) : isRegionManagedAccount ? (
                            <FlexibleFormAlert
                                tone="warning"
                                message={
                                    <>
                                        It looks like this account is managed in another region and cannot be managed
                                        here. Please use the account center for that region, or chat with an agent for
                                        assistance.
                                    </>
                                }
                            />
                        ) : isNonStandardIdentity ? (
                            <FlexibleFormAlert
                                tone="warning"
                                message={
                                    <>
                                        This account needs a different sign-in flow before it can be managed here.
                                        Please sign in with your account login, or chat with an agent for assistance.
                                    </>
                                }
                            />
                        ) : undefined
                    }
                    appdownloadItems={resolvedAppDownloadItems}
                    hasError={errorType !== 'none'}
                    identifier={identifier}
                    invalidMessage={invalidMessage}
                    isSubmitting={isSubmitting}
                    onBack={handleCredentialBack}
                    onPasswordChange={handlePasswordChange}
                    onSubmit={handlePasswordSubmit}
                    password={password}
                />
            ) : step === 'enterVerificationCode' ? (
                <div className={`${styles.snapshotUserFlow} ${styles.snapshotVerificationFlow}`}>
                    <div className={styles.snapshotSubscreenTopBar}>
                        <button
                            aria-label="Go back"
                            className={styles.snapshotBackButton}
                            onClick={handleCredentialBack}
                            type="button"
                        >
                            <IconArrow />
                        </button>
                    </div>

                    <div className={styles.snapshotDivider}>
                        <div className={styles.snapshotDividerLine} />
                    </div>

                    <div className={styles.snapshotSubscreenMain}>
                        <div className={styles.snapshotSubscreenSection}>
                            <SignInVerificationCodeBody
                                destinationLabel={selectedMethodDescription}
                                invalidMessage={invalidMessage}
                                isSubmitting={isSubmitting}
                                notificationMessage={verificationNotification?.message}
                                notificationMessageKey={verificationNotification?.id}
                                notificationPhase={verificationNotification?.phase}
                                notificationBanner={methodNotificationBanner}
                                onRequestNewCode={handleRequestNewCode}
                                onSubmit={handleVerificationCodeSubmit}
                                onVerificationCodeBlur={handleVerificationCodeBlur}
                                onVerificationCodeChange={handleVerificationCodeChange}
                                showErrorSummary={
                                    hasSubmittedVerificationCodeStep &&
                                    errorType !== 'none' &&
                                    errorType !== 'verificationCodeRateLimited'
                                }
                                verificationCode={verificationCode}
                            />
                        </div>
                    </div>

                    <div className={styles.snapshotDivider}>
                        <div className={styles.snapshotDividerLine} />
                    </div>

                    <div className={styles.snapshotAnonymousFooter}>
                        <Text tag="h4" className={styles.snapshotAppHeading}>
                            Get the SiriusXM app
                        </Text>
                        <AppDownloadButtons
                            items={resolvedAppDownloadItems}
                            className={styles.snapshotDownloadButtons}
                            appleClassName={styles.snapshotStoreBadge}
                            androidClassName={styles.snapshotStoreBadge}
                        />
                    </div>
                </div>
            ) : isPartnerSubscriptionStep ? (
                <div className={styles.snapshotUserFlow}>
                    <div className={styles.snapshotSubscreenTopBar}>
                        <button
                            aria-label="Go back"
                            className={styles.snapshotBackButton}
                            onClick={handlePartnerBack}
                            type="button"
                        >
                            <IconArrow />
                        </button>
                    </div>

                    <div className={styles.snapshotDivider}>
                        <div className={styles.snapshotDividerLine} />
                    </div>

                    <div
                        className={[
                            styles.snapshotSubscreenMainBody,
                            !shouldShowPartnerSubscriptionFooter ? styles.snapshotSubscreenMainBodyWithoutFooter : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        <div className={styles.snapshotSubscreenSection}>
                            {inAppPurchaseVariant ? (
                                <InAppPurchasePopover
                                    chatClickHandler={(event) => {
                                        event.preventDefault();
                                        const clientWindow = window as BrowserWindow;
                                        clientWindow?.sierraSXM?.openChatModal();
                                    }}
                                    embedded={true}
                                    onDismissClick={(event) => {
                                        event.preventDefault();
                                        handlePartnerDismiss();
                                    }}
                                    subscribeHref={inAppPurchaseSubscribeHref}
                                    variant={inAppPurchaseVariant}
                                />
                            ) : teslaManageSelection ? (
                                <div className={styles.snapshotManageSelection}>
                                    <Text tag="h6" variant="h6" className={styles.snapshotManageSelectionTitle}>
                                        Which subscription would you like to manage?
                                    </Text>
                                    <div className={styles.snapshotManageSelectionCards}>
                                        <div className={styles.snapshotManageSelectionCard}>
                                            <div className={styles.snapshotManageSelectionCardCopy}>
                                                <Text
                                                    tag="p"
                                                    variant="h6"
                                                    className={styles.snapshotManageSelectionCardTitle}
                                                >
                                                    {teslaManageSelection.vehicleTitle}
                                                </Text>
                                                <Text
                                                    tag="p"
                                                    variant="text-medium"
                                                    className={styles.snapshotManageSelectionCardSubtitle}
                                                >
                                                    {teslaManageSelection.vehicleSubtitle}
                                                </Text>
                                            </div>
                                            <Button
                                                type="button"
                                                size="small"
                                                variant="secondary"
                                                fitting="fill-container"
                                                className={styles.snapshotManageSelectionButton}
                                                href={teslaManageSelection.vehicleHref}
                                            >
                                                Manage
                                            </Button>
                                        </div>
                                        <div className={styles.snapshotManageSelectionCard}>
                                            <div className={styles.snapshotManageSelectionCardCopy}>
                                                <Text
                                                    tag="p"
                                                    variant="h6"
                                                    className={styles.snapshotManageSelectionCardTitle}
                                                >
                                                    {teslaManageSelection.streamingTitle}
                                                </Text>
                                                <Text
                                                    tag="p"
                                                    variant="text-medium"
                                                    className={styles.snapshotManageSelectionCardSubtitle}
                                                >
                                                    {teslaManageSelection.streamingSubtitle}
                                                </Text>
                                            </div>
                                            <Button
                                                type="button"
                                                size="small"
                                                variant="secondary"
                                                fitting="fill-container"
                                                className={styles.snapshotManageSelectionButton}
                                                href={teslaManageSelection.streamingHref}
                                            >
                                                Manage
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ) : partnerSubscriptionVariant ? (
                                <PartnerSubscription variant={partnerSubscriptionVariant} />
                            ) : null}
                        </div>
                    </div>

                    {shouldShowPartnerSubscriptionFooter ? (
                        <>
                            <div className={styles.snapshotDivider}>
                                <div className={styles.snapshotDividerLine} />
                            </div>

                            <div className={styles.snapshotAnonymousFooter}>
                                <Text tag="h4" className={styles.snapshotAppHeading}>
                                    Get the SiriusXM app
                                </Text>
                                <AppDownloadButtons
                                    items={resolvedAppDownloadItems}
                                    className={styles.snapshotDownloadButtons}
                                    appleClassName={styles.snapshotStoreBadge}
                                    androidClassName={styles.snapshotStoreBadge}
                                />
                            </div>
                        </>
                    ) : null}
                </div>
            ) : (
                <SnapshotUserAnonymousSignIn
                    appdownloadItems={resolvedAppDownloadItems}
                    headingId={headingId}
                    hasFieldError={errorType !== 'none'}
                    identifier={identifier}
                    invalidMessage={invalidMessage}
                    isSubmitting={isSubmitting}
                    onIdentifierBlur={handleIdentifierBlur}
                    onIdentifierChange={handleIdentifierChange}
                    onSubmit={handleSubmit}
                    showErrorSummary={hasSubmittedIdentifierStep && errorType !== 'none'}
                />
            )}
        </SnapshotPopoverShell>
    );
}
